import { CBData, KCSDK } from "@/sdk"
import { store, useAppSelector } from "@/store"
import { selectIsSafariBrowser } from "@/stores/app_slice"
import { buffer2String, calculateMD5, string2Buffer } from "@/utils/tools"
import Pako from "pako"
import { useEffect } from "react"

// 最近一次本地粘贴板内容的哈希值
let lastLocalCBContentMD5: string | null = null
let pendingRemoteClipboardText: string | null = null
let lastKnownLocalClipboardText: string | null = null
let lastSafariClipboardSource: "local" | "remote" | null = null
let safariClipboardBridge: HTMLTextAreaElement | null = null
let safariClipboardWriteResolver: ((data: Blob) => void) | null = null
let safariClipboardWriteRejecter: ((error?: Error) => void) | null = null
let safariClipboardWriteTimeout: ReturnType<typeof setTimeout> | null = null
let safariRemotePasteResolver: ((success: boolean) => void) | null = null
let safariRemotePasteTimeout: ReturnType<typeof setTimeout> | null = null

function uint8ArrayToArrayBuffer(data: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(data.byteLength)
  new Uint8Array(buffer).set(data)
  return buffer
}

function isSafariBrowser() {
  return selectIsSafariBrowser(store.getState())
}

function ensureSafariClipboardBridge() {
  if (safariClipboardBridge !== null) {
    return safariClipboardBridge
  }

  const textarea = document.createElement("textarea")
  textarea.setAttribute("aria-hidden", "true")
  textarea.setAttribute("tabindex", "-1")
  textarea.autocomplete = "off"
  textarea.autocapitalize = "off"
  textarea.spellcheck = false
  textarea.style.position = "fixed"
  textarea.style.left = "-9999px"
  textarea.style.top = "0"
  textarea.style.width = "1px"
  textarea.style.height = "1px"
  textarea.style.opacity = "0"
  textarea.style.pointerEvents = "none"
  document.body.appendChild(textarea)
  safariClipboardBridge = textarea
  return textarea
}

function focusSafariClipboardBridge() {
  if (!isSafariBrowser()) {
    return
  }

  const bridge = ensureSafariClipboardBridge()
  if (document.activeElement !== bridge) {
    bridge.focus({ preventScroll: true })
  }
}

function setSafariClipboardBridgeText(text: string) {
  const bridge = ensureSafariClipboardBridge()
  bridge.value = text
  bridge.focus({ preventScroll: true })
  bridge.select()
  bridge.setSelectionRange(0, text.length)
}

function clearSafariClipboardBridge() {
  if (safariClipboardBridge === null) {
    return
  }

  safariClipboardBridge.value = ""
}

function clearSafariPendingClipboardWrite() {
  if (safariClipboardWriteTimeout !== null) {
    clearTimeout(safariClipboardWriteTimeout)
    safariClipboardWriteTimeout = null
  }

  safariClipboardWriteResolver = null
  safariClipboardWriteRejecter = null
}

function clearSafariPendingRemotePaste() {
  if (safariRemotePasteTimeout !== null) {
    clearTimeout(safariRemotePasteTimeout)
    safariRemotePasteTimeout = null
  }

  safariRemotePasteResolver = null
}

function rejectSafariPendingClipboardWrite(message: string) {
  if (safariClipboardWriteRejecter !== null) {
    safariClipboardWriteRejecter(new Error(message))
  }
  clearSafariPendingClipboardWrite()
}

function isClipboardPageReady() {
  if (!window.document.hasFocus()) {
    console.warn("Clipboard cannot access when the page is not focused")
    return false
  }

  return true
}

function syncTextToRemoteClipboard(text: string) {
  if (!text) {
    return
  }

  lastKnownLocalClipboardText = text
  lastSafariClipboardSource = "local"
  const textBuffer = string2Buffer(text)
  const hash = calculateMD5(textBuffer)
  if (hash === lastLocalCBContentMD5) {
    return
  }
  lastLocalCBContentMD5 = hash

  KCSDK.setRemoteClipboardData(
    "ClipboardText",
    uint8ArrayToArrayBuffer(Pako.deflate(textBuffer))
  )
}

async function syncLocalClipboardTextToRemoteByGesture() {
  if (!window.navigator.clipboard) {
    return false
  }

  if (window.navigator.clipboard.readText) {
    const text = await window.navigator.clipboard.readText()
    if (!text) {
      return false
    }
    syncTextToRemoteClipboard(text)
    return true
  }

  if (!window.navigator.clipboard.read) {
    return false
  }

  const items = await window.navigator.clipboard.read()
  if (items.length === 0) {
    return false
  }

  const item = items[0]
  const textType = item.types.find((type) => type.startsWith("text"))
  if (!textType) {
    return false
  }

  const blobData = await item.getType(textType)
  const text = await blobData.text()
  if (!text) {
    return false
  }

  syncTextToRemoteClipboard(text)
  return true
}

function syncCachedLocalClipboardTextToRemote() {
  if (!lastKnownLocalClipboardText) {
    return false
  }

  syncTextToRemoteClipboard(lastKnownLocalClipboardText)
  return true
}

async function writeTextToLocalClipboard(text: string) {
  if (window.navigator.clipboard?.writeText) {
    await window.navigator.clipboard.writeText(text)
    return
  }

  throw new Error("Clipboard write API not supported")
}

function markLocalClipboardText(text: string) {
  pendingRemoteClipboardText = null
  lastKnownLocalClipboardText = text
  lastLocalCBContentMD5 = calculateMD5(string2Buffer(text))
}

function shouldUseRemoteClipboardForSafariPaste() {
  return (
    lastSafariClipboardSource === "remote" &&
    pendingRemoteClipboardText !== null &&
    pendingRemoteClipboardText.length > 0
  )
}

function shouldUseCachedLocalClipboardForSafariPaste() {
  return (
    lastSafariClipboardSource === "local" &&
    lastKnownLocalClipboardText !== null &&
    lastKnownLocalClipboardText.length > 0
  )
}

function prepareSafariClipboardWriteFromRemote() {
  if (
    !isSafariBrowser() ||
    !window.navigator.clipboard?.write ||
    typeof ClipboardItem === "undefined"
  ) {
    return false
  }

  rejectSafariPendingClipboardWrite("Clipboard write request superseded")

  const textPromise = new Promise<Blob>((resolve, reject) => {
    safariClipboardWriteResolver = resolve
    safariClipboardWriteRejecter = reject
  })

  safariClipboardWriteTimeout = setTimeout(() => {
    rejectSafariPendingClipboardWrite("Safari clipboard write timed out")
  }, 3000)

  window.navigator.clipboard
    .write([
      new ClipboardItem({
        "text/plain": textPromise,
      }),
    ])
    .catch((error) => {
      console.warn("Safari clipboard async write error: ", error)
      clearSafariPendingClipboardWrite()
    })

  return true
}

function resolveSafariClipboardWriteFromRemote(text: string) {
  if (safariClipboardWriteResolver === null) {
    return false
  }

  safariClipboardWriteResolver(new Blob([text], { type: "text/plain" }))
  clearSafariPendingClipboardWrite()
  markLocalClipboardText(text)
  return true
}

async function handleSafariPasteShortcutToRemote() {
  if (!isSafariBrowser() || !isClipboardPageReady()) {
    return await Promise.resolve(false)
  }

  clearSafariPendingRemotePaste()
  const bridge = ensureSafariClipboardBridge()
  bridge.value = ""
  bridge.focus({ preventScroll: true })
  bridge.select()
  bridge.setSelectionRange(0, 0)

  return await new Promise<boolean>((resolve) => {
    safariRemotePasteResolver = resolve
    safariRemotePasteTimeout = setTimeout(() => {
      clearSafariPendingRemotePaste()
      resolve(false)
    }, 3000)
  })
}

const useSyncLocalCPToRemote = () => {
  const isSafari = useAppSelector(selectIsSafariBrowser)

  // 将本地设备的粘贴板同步到远端
  const syncLocalCPToRemote = async () => {
    if (!isClipboardPageReady()) {
      return
    }

    // 检测粘贴板权限
    let res
    try {
      // 检测粘贴板权限
      if (window.navigator.permissions?.query) {
        res = await window.navigator.permissions.query({
          name: "clipboard-read" as PermissionName,
        })
      } else {
        console.warn("Permissions API not supported")
        return
      }
    } catch (error) {
      console.warn("Error querying clipboard permissions: ", error)
      return
    }

    if (res.state === "denied") {
      return
    }

    try {
      // 读取粘贴板数据
      const items = await window.navigator.clipboard.read()

      if (items.length === 0) {
        return
      }
      const item = items[0]
      const type = item.types[0]
      if (!type) {
        return
      }

      // 粘贴板数据
      const blobData = await item.getType(type)

      const bufferData = await blobData.arrayBuffer()

      // 粘贴板数据类型
      let dataType: string | null = null
      let payloadData = bufferData
      const isText = type.startsWith("text")
      // const isImage = type.startsWith("image")
      if (isText) {
        dataType = "ClipboardText"
        payloadData = uint8ArrayToArrayBuffer(Pako.deflate(bufferData))
      }

      if (dataType === null) {
        // 不支持的数据类型
        return
      }

      const hash = calculateMD5(bufferData)
      if (hash === lastLocalCBContentMD5) {
        // 粘贴板内容已同步过
        return
      }
      lastLocalCBContentMD5 = hash

      // 设置远端设备粘贴板
      KCSDK.setRemoteClipboardData(dataType, payloadData)
    } catch (e) {
      console.warn("Clipboard read error: ", e)
    }
  }

  // 当页面获取焦点时，将本地设备的粘贴板同步到远端
  const onFocus = (_event: FocusEvent) => {
    if (isSafari) {
      focusSafariClipboardBridge()
      return
    }

    syncLocalCPToRemote()
  }

  const onSafariPaste = (event: ClipboardEvent) => {
    if (!isSafari || !isClipboardPageReady()) {
      return
    }

    const text =
      event.clipboardData?.getData("text/plain") ??
      event.clipboardData?.getData("text") ??
      ""

    if (text) {
      syncTextToRemoteClipboard(text)
      if (safariRemotePasteResolver !== null) {
        safariRemotePasteResolver(true)
        clearSafariPendingRemotePaste()
      }
      return
    }

    if (safariRemotePasteResolver !== null) {
      safariRemotePasteResolver(false)
      clearSafariPendingRemotePaste()
    }
    focusSafariClipboardBridge()
  }

  const onSafariCopy = (event: ClipboardEvent) => {
    if (!isSafari || pendingRemoteClipboardText === null) {
      return
    }

    if (!event.clipboardData) {
      return
    }

    event.clipboardData.setData("text/plain", pendingRemoteClipboardText)
    event.preventDefault()
    setSafariClipboardBridgeText(pendingRemoteClipboardText)
    markLocalClipboardText(pendingRemoteClipboardText)
  }

  useEffect(() => {
    if (isSafari) {
      ensureSafariClipboardBridge()
      focusSafariClipboardBridge()
    }

    window.addEventListener("focus", onFocus)
    document.addEventListener("paste", onSafariPaste)
    document.addEventListener("copy", onSafariCopy)

    const dispose = () => {
      window.removeEventListener("focus", onFocus)
      document.removeEventListener("paste", onSafariPaste)
      document.removeEventListener("copy", onSafariCopy)
    }

    KCSDK.setOnRemoteCopy((data) => {
      syncRemoteCBToLocal(data).catch((error) => {
        console.warn("Clipboard sync to local error: ", error)
      })
    })
    return dispose
  }, [isSafari])
}

// 将远端设备的粘贴板同步到本地
async function syncRemoteCBToLocal(cbData: CBData) {
  const isSafari = isSafariBrowser()

  if (!isClipboardPageReady()) {
    console.warn("Clipboard cannot write when the page is not focused")
    return
  }

  const { type, data } = cbData

  try {
    if (window.navigator.permissions?.query) {
      const res = await window.navigator.permissions.query({
        name: "clipboard-write" as PermissionName,
      })

      if (res.state === "denied") {
        return
      }
    } else {
      console.warn(
        "Permissions API not supported, fallback to direct clipboard write"
      )
    }
  } catch (error) {
    console.warn(
      "Error querying clipboard write permissions, fallback to direct clipboard write: ",
      error
    )
  }

  if (type !== "ClipboardText") {
    return
  }

  try {
    const originData = Pako.ungzip(data)
    const hash = calculateMD5(originData)
    if (hash === lastLocalCBContentMD5) {
      // 粘贴板内容已同步过
      return
    }

    const text = buffer2String(originData)
    pendingRemoteClipboardText = text
    lastSafariClipboardSource = "remote"
    if (isSafari) {
      setSafariClipboardBridgeText(text)
      if (resolveSafariClipboardWriteFromRemote(text)) {
        return
      }
    }

    if (isSafari && !window.navigator.clipboard?.writeText) {
      return
    }

    await writeTextToLocalClipboard(text)
    markLocalClipboardText(text)
  } catch (error) {
    console.warn("Clipboard write error: ", error)
  }
}

// 连接断开时清理资源
function onDisconnect() {
  lastLocalCBContentMD5 = null
  pendingRemoteClipboardText = null
  lastKnownLocalClipboardText = null
  lastSafariClipboardSource = null
  clearSafariClipboardBridge()
  rejectSafariPendingClipboardWrite("Clipboard session disconnected")
  clearSafariPendingRemotePaste()
}

export const ClipboardUsecase = {
  handleSafariPasteShortcutToRemote,
  prepareSafariClipboardWriteFromRemote,
  shouldUseCachedLocalClipboardForSafariPaste,
  shouldUseRemoteClipboardForSafariPaste,
  syncCachedLocalClipboardTextToRemote,
  syncLocalClipboardTextToRemoteByGesture,
  useSyncLocalCPToRemote,
  syncRemoteCBToLocal,
  onDisconnect,
}
